import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, GripHorizontal, X, Delete, CornerDownLeft, History, Maximize2, Minimize2 } from "lucide-react";
import CalculatorTapeWindow from "./CalculatorTapeWindow";

const POS_KEY = "calc_panel_pos";
const PANEL_W = 300;

// Read the last dragged position, clamped back into view (window may have
// shrunk since, which would otherwise strand the panel offscreen).
const readPos = () => {
  const fallback = { x: Math.max(12, window.innerWidth - PANEL_W - 24), y: 96 };
  try {
    const saved = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    if (!saved || typeof saved.x !== "number" || typeof saved.y !== "number") return fallback;
    return {
      x: Math.min(Math.max(4, saved.x), Math.max(4, window.innerWidth - PANEL_W - 4)),
      y: Math.min(Math.max(4, saved.y), Math.max(4, window.innerHeight - 120)),
    };
  } catch {
    return fallback;
  }
};

const fmt = (n) => {
  if (!isFinite(n)) return "Error";
  // Trim float noise (0.1+0.2) without killing precision on real values.
  const r = Math.round(n * 1e10) / 1e10;
  return String(r);
};

// Indian grouping — 1,23,456.78 — because every figure here is rupees.
const inr = (n) =>
  (isFinite(n) ? n : 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const num = (s) => {
  const v = parseFloat(String(s).replace(/,/g, ""));
  return isFinite(v) ? v : 0;
};

const OPS = { "+": (a, b) => a + b, "-": (a, b) => a - b, "*": (a, b) => a * b, "/": (a, b) => a / b };
const OP_LABEL = { "+": "+", "-": "−", "*": "×", "/": "÷" };

// Attach the running subtotal to each operand of a step list. The keypad is
// strictly left-to-right (an operator evaluates the pending pair immediately),
// so a plain fold matches exactly what the machine did — that is the whole
// point of the tape: check the figure line by line.
const withRunning = (list) => {
  let run = 0;
  return list.map((s, i) => {
    run = i === 0 || !OPS[s.op] ? s.value : OPS[s.op](run, s.value);
    return { ...s, run };
  });
};

const GST_RATES = [0, 0.25, 3, 5, 12, 18, 28];

// Two-input tools. Each declares its field labels, quick-rate chips and the
// result lines it produces, so one compact form serves all of them.
const TOOLS = {
  discount: {
    label: "Discount",
    a: "Amount", b: "Discount %", chips: [2, 5, 10, 15, 20, 25],
    calc: (a, b) => [["Discount", (a * b) / 100], ["Net amount", a - (a * b) / 100, true]],
  },
  tds: {
    label: "TDS (deduct)",
    a: "Amount", b: "TDS %", chips: [0.1, 1, 2, 5, 10],
    calc: (a, b) => [["TDS", (a * b) / 100], ["Net payable", a - (a * b) / 100, true]],
  },
  tcs: {
    label: "TCS (collect)",
    a: "Amount", b: "TCS %", chips: [0.075, 0.1, 0.5, 1],
    calc: (a, b) => [["TCS", (a * b) / 100], ["Total receivable", a + (a * b) / 100, true]],
  },
  markup: {
    label: "Cost + Markup",
    a: "Cost", b: "Markup %", chips: [5, 10, 15, 20, 25, 30],
    calc: (a, b) => [["Profit", (a * b) / 100], ["Selling price", a + (a * b) / 100, true]],
  },
  margin: {
    label: "Margin → Price",
    a: "Cost", b: "Margin %", chips: [5, 10, 15, 20, 25, 30],
    calc: (a, b) => {
      const price = b >= 100 ? NaN : a / (1 - b / 100);
      return [["Profit", price - a], ["Selling price", price, true]];
    },
  },
  qtyrate: {
    label: "Qty × Rate",
    a: "Qty / Wt", b: "Rate", chips: null,
    calc: (a, b) => [["Amount", a * b, true]],
  },
  share: {
    label: "% of Amount",
    a: "Amount", b: "Percent %", chips: [1, 2, 5, 10, 18, 50],
    calc: (a, b) => [["Percent value", (a * b) / 100, true], ["Balance", a - (a * b) / 100]],
  },
};

/**
 * Floating mini calculator with the accounting maths this ERP actually needs.
 * Deliberately NOT a modal dialog — an accountant needs to read figures off
 * the invoice behind it while working, so it is a draggable always-on-top card
 * with no overlay.
 *
 * Tabs: Calc (keypad + tape) · GST (add/remove, CGST-SGST vs IGST) · Tools
 * (discount, TDS/TCS, markup, margin, qty × rate).
 */
export default function CalculatorPanel({ open, onClose }) {
  const [tab, setTab] = useState("calc");
  const [display, setDisplay] = useState("0");
  const [acc, setAcc] = useState(null);     // pending left-hand value
  const [op, setOp] = useState(null);       // pending operator
  const [overwrite, setOverwrite] = useState(true); // next digit replaces display
  const [tape, setTape] = useState([]);     // running history, newest first
  const [steps, setSteps] = useState([]);   // operands of the calc in progress
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tapeOpen, setTapeOpen] = useState(false); // expanded step-by-step tape
  const [copied, setCopied] = useState(null); // key of the last copied line
  const [pos, setPos] = useState(readPos);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const seqRef = useRef(0);

  // GST tab
  const [gstAmt, setGstAmt] = useState("");
  const [gstRate, setGstRate] = useState(18);
  const [gstMode, setGstMode] = useState("add");     // add | remove (inclusive)
  const [interState, setInterState] = useState(false); // IGST vs CGST+SGST

  // Tools tab
  const [tool, setTool] = useState("discount");
  const [toolA, setToolA] = useState("");
  const [toolB, setToolB] = useState("");

  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true });
  }, [open]);

  const copyVal = useCallback((key, value) => {
    const text = typeof value === "number" ? (isFinite(value) ? value.toFixed(2) : "0") : String(value);
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1200);
    }).catch(() => {});
  }, []);

  // ── keypad actions ────────────────────────────────────────────────────
  const inputDigit = useCallback((d) => {
    setDisplay((cur) => {
      if (overwrite || cur === "Error") return d;
      if (cur === "0") return d;
      if (cur.replace("-", "").replace(".", "").length >= 14) return cur;
      return cur + d;
    });
    setOverwrite(false);
  }, [overwrite]);

  const inputDot = useCallback(() => {
    setDisplay((cur) => {
      if (overwrite || cur === "Error") return "0.";
      return cur.includes(".") ? cur : cur + ".";
    });
    setOverwrite(false);
  }, [overwrite]);

  const clearAll = useCallback(() => {
    setDisplay("0"); setAcc(null); setOp(null); setOverwrite(true); setSteps([]);
  }, []);

  const backspace = useCallback(() => {
    if (overwrite) return;
    setDisplay((cur) => {
      const next = cur.slice(0, -1);
      return next === "" || next === "-" ? "0" : next;
    });
  }, [overwrite]);

  const toggleSign = useCallback(() => {
    setDisplay((cur) => (cur.startsWith("-") ? cur.slice(1) : cur === "0" ? cur : "-" + cur));
  }, []);

  // "200 + 10%" → 10% OF 200 (what an invoice/tax calc expects); bare "50%" → 0.5
  const percent = useCallback(() => {
    setDisplay((cur) => {
      const v = parseFloat(cur) || 0;
      const base = acc !== null && (op === "+" || op === "-") ? acc : 1;
      return fmt((v / 100) * base);
    });
    setOverwrite(false);
  }, [acc, op]);

  const chooseOp = useCallback((nextOp) => {
    const cur = parseFloat(display) || 0;
    if (acc !== null && op && !overwrite) {
      const res = OPS[op](acc, cur);
      setAcc(res);
      setDisplay(fmt(res));
      setSteps((s) => [...s, { op, value: cur }]);       // operand just consumed
    } else if (acc === null) {
      setAcc(cur);
      setSteps([{ op: null, value: cur }]);              // first operand
    }
    // acc set but overwrite → user only swapped the pending operator; steps stand
    setOp(nextOp);
    setOverwrite(true);
  }, [display, acc, op, overwrite]);

  // Stable id per entry — the check window hangs its per-card progress off it,
  // and the list index shifts every time a new calculation lands on top.
  const pushTape = useCallback((expr, value, entrySteps) => {
    seqRef.current += 1;
    const id = seqRef.current;
    setTape((t) => [{ id, expr, value, steps: entrySteps }, ...t].slice(0, 30));
  }, []);

  const equals = useCallback(() => {
    if (acc === null || !op) return;
    const rhs = parseFloat(display) || 0;
    const res = OPS[op](acc, rhs);
    const full = [...steps, { op, value: rhs }];
    pushTape(full.map((s, i) => (i ? `${OP_LABEL[s.op]} ${fmt(s.value)}` : fmt(s.value))).join(" "), res, full);
    setDisplay(fmt(res));
    setAcc(null); setOp(null); setOverwrite(true); setSteps([]);
  }, [acc, op, display, steps, pushTape]);

  // Send any computed figure back to the keypad as the live value.
  const toCalc = useCallback((value, note) => {
    if (!isFinite(value)) return;
    setDisplay(fmt(Math.round(value * 100) / 100));
    setAcc(null); setOp(null); setOverwrite(true); setSteps([]);
    if (note) pushTape(note, value);
    setTab("calc");
  }, [pushTape]);

  /**
   * One key router for both the focused panel and the global fallback.
   * Numpad keys are matched on e.code FIRST so they still work with NumLock
   * OFF (where Numpad4 reports e.key "ArrowLeft", not "4").
   * Returns true when the key was consumed.
   */
  const handleKey = useCallback((e) => {
    if (e.ctrlKey || e.altKey || e.metaKey) return false;
    const code = e.code || "";
    const k = e.key;

    if (code.startsWith("Numpad")) {
      const n = code.slice(6); // "0".."9" | "Decimal" | "Add" | ...
      if (n >= "0" && n <= "9") inputDigit(n);
      else if (n === "Decimal" || n === "Separator") inputDot();
      else if (n === "Add") chooseOp("+");
      else if (n === "Subtract") chooseOp("-");
      else if (n === "Multiply") chooseOp("*");
      else if (n === "Divide") chooseOp("/");
      else if (n === "Enter" || n === "Equal") equals();
      else return false;
      return true;
    }

    if (k >= "0" && k <= "9") inputDigit(k);
    else if (k === "." || k === ",") inputDot();
    else if (k === "+" || k === "-") chooseOp(k);
    else if (k === "*" || k === "x" || k === "X") chooseOp("*");
    else if (k === "/") chooseOp("/");
    else if (k === "Enter" || k === "=") equals();
    else if (k === "Backspace") backspace();
    else if (k === "%") percent();
    else if (k === "c" || k === "C" || k === "Delete") clearAll();
    else return false;
    return true;
  }, [inputDigit, inputDot, chooseOp, equals, backspace, percent, clearAll]);

  // Panel-focused handler — also owns Escape (close), which the global
  // fallback deliberately does NOT, so Esc keeps closing whatever dialog the
  // user actually has focused.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); e.preventDefault(); return; }
    if (tab !== "calc") return;                    // GST/Tools use real inputs
    if (!handleKey(e)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  // Global fallback: while the calculator is open, number/operator keys typed
  // with no editable field focused go to it. Without this the numpad only
  // works right after you click the panel — click any table row and it dies.
  useEffect(() => {
    if (!open) return;
    const isEditable = (el) =>
      !!el && (
        ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
        el.isContentEditable ||
        el.getAttribute?.("role") === "textbox" ||
        el.closest?.('[contenteditable="true"]')
      );
    const onGlobalKey = (e) => {
      const t = e.target;
      if (isEditable(t)) return;                       // user is typing somewhere real
      if (panelRef.current?.contains(t)) return;       // panel handler already has it
      // Plain Enter/Escape belong to whatever is focused (buttons, dialogs).
      if (e.key === "Enter" && e.code !== "NumpadEnter") return;
      if (e.key === "Escape") return;
      if (!handleKey(e)) return;
      if (tab !== "calc") setTab("calc");              // show what they're typing into
      e.preventDefault();
    };
    window.addEventListener("keydown", onGlobalKey);
    return () => window.removeEventListener("keydown", onGlobalKey);
  }, [open, handleKey, tab]);

  // ── GST maths ─────────────────────────────────────────────────────────
  const gst = useMemo(() => {
    const amt = num(gstAmt);
    const rate = num(gstRate);
    const taxable = gstMode === "add" ? amt : (amt * 100) / (100 + rate);
    const tax = gstMode === "add" ? (amt * rate) / 100 : amt - taxable;
    const total = taxable + tax;
    const rounded = Math.round(total);
    return {
      taxable, tax, total, rounded, roundOff: rounded - total,
      cgst: interState ? 0 : tax / 2,
      sgst: interState ? 0 : tax / 2,
      igst: interState ? tax : 0,
    };
  }, [gstAmt, gstRate, gstMode, interState]);

  // ── the tape ──────────────────────────────────────────────────────────
  // Rows for the calculation currently in progress. Skipped entirely when the
  // keypad is idle (fresh 0, or a result waiting to be replaced) so a finished
  // sum is not echoed a second time under its own "=" line.
  const liveRows = useMemo(() => {
    if (steps.length === 0 && !op && overwrite) return [];
    const rows = withRunning(steps);
    if (op) rows.push({ op, value: overwrite ? null : parseFloat(display) || 0, run: null, pending: true });
    else if (steps.length === 0) rows.push({ op: null, value: parseFloat(display) || 0, run: null });
    return rows;
  }, [steps, op, overwrite, display]);

  // Flattened, pre-formatted cards for the check window — oldest calculation
  // first, each operand then its result, with whatever is being typed last.
  const reviewItems = useMemo(() => {
    const out = [];
    [...tape].reverse().forEach((t, gi) => {
      const rows = t.steps?.length ? withRunning(t.steps) : [];
      rows.forEach((s, j) =>
        out.push({
          id: `${t.id}:${j}`,
          sym: j === 0 ? "" : OP_LABEL[s.op],
          text: fmt(s.value),
          runText: j === 0 ? "" : fmt(s.run),
          label: `#${gi + 1} · step ${j + 1}`,
        })
      );
      out.push({
        id: `${t.id}:=`,
        sym: "=",
        text: fmt(t.value),
        runText: rows.length ? "" : t.expr,   // pushed-in figures carry their source note
        label: `#${gi + 1} · total`,
        result: true,
      });
    });
    liveRows.forEach((r, i) =>
      out.push({
        id: `live:${i}`,
        sym: r.op ? OP_LABEL[r.op] : "",
        text: r.value == null ? "…" : fmt(r.value),
        runText: r.run != null && i > 0 ? fmt(r.run) : "",
        label: `current · step ${i + 1}`,
        live: true,
      })
    );
    return out;
  }, [tape, liveRows]);

  const toolDef = TOOLS[tool];
  const toolLines = useMemo(
    () => toolDef.calc(num(toolA), num(toolB)),
    [toolDef, toolA, toolB]
  );

  // Moving between tabs carries the current figure over so you never retype it.
  const switchTab = (next) => {
    if (next === "gst" && !gstAmt) setGstAmt(display === "0" ? "" : display);
    if (next === "tools" && !toolA) setToolA(display === "0" ? "" : display);
    setTab(next);
  };

  // ── drag ──────────────────────────────────────────────────────────────
  const startDrag = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragRef.current = { dx: point.clientX - pos.x, dy: point.clientY - pos.y };
    e.preventDefault();
  };

  useEffect(() => {
    if (!open) return;
    const move = (e) => {
      if (!dragRef.current) return;
      const point = e.touches ? e.touches[0] : e;
      const x = Math.min(Math.max(4, point.clientX - dragRef.current.dx), window.innerWidth - PANEL_W - 4);
      const y = Math.min(Math.max(4, point.clientY - dragRef.current.dy), window.innerHeight - 60);
      setPos({ x, y });
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setPos((p) => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} return p; });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [open]);

  if (!open) return null;

  const key =
    "h-9 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 select-none";
  const numKey = `${key} bg-muted/60 text-foreground hover:bg-muted tabular-nums`;
  const fnKey = `${key} bg-muted/30 text-muted-foreground hover:bg-muted/60`;
  const opKey = `${key} bg-primary/10 text-primary hover:bg-primary/20`;
  const fieldCls =
    "w-full h-8 px-2 rounded-md border border-border bg-background text-sm font-mono tabular-nums text-right focus:outline-none focus:ring-2 focus:ring-primary/40";
  const chipCls = (active) =>
    `h-6 px-1.5 rounded text-[11px] font-medium tabular-nums transition-colors ${
      active ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground hover:bg-muted"
    }`;

  const Btn = ({ className, onClick, children, label }) => (
    <button type="button" className={className} onClick={onClick} aria-label={label} tabIndex={-1}>
      {children}
    </button>
  );

  // A result line: click to copy, arrow to push the figure into the keypad.
  const Line = ({ id, label, value, strong, note }) => (
    <div className="flex items-center gap-1 group">
      <button
        type="button"
        onClick={() => copyVal(id, value)}
        className="flex-1 flex items-center justify-between gap-2 px-2 py-1 rounded-md hover:bg-muted/60 text-left"
        title="Click to copy"
      >
        <span className="text-[11px] text-muted-foreground truncate">{label}</span>
        <span className={`font-mono tabular-nums ${strong ? "text-sm font-semibold" : "text-xs"}`}>
          {copied === id ? "copied" : inr(value)}
        </span>
      </button>
      <button
        type="button"
        onClick={() => toCalc(value, note || label)}
        title="Send to calculator"
        className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-muted"
      >
        <CornerDownLeft className="w-3 h-3" />
      </button>
    </div>
  );

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => switchTab(id)}
      data-testid={`calc-tab-${id}`}
      className={`flex-1 h-7 text-[11px] font-semibold rounded-md transition-colors ${
        tab === id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
    <div
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => {
        // Don't yank focus off the GST/Tools fields the user just clicked.
        if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
        panelRef.current?.focus({ preventScroll: true });
      }}
      data-testid="calculator-panel"
      style={{ left: pos.x, top: pos.y, width: PANEL_W }}
      className="fixed z-[200] rounded-xl border border-border bg-card shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {/* header / drag handle */}
      <div
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className="flex items-center gap-1.5 px-2.5 h-9 border-b border-border cursor-move"
      >
        <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold tracking-tight flex-1">Calculator</span>
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          title="History"
          data-testid="calculator-history-btn"
          className={`relative h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted ${
            historyOpen ? "text-foreground bg-muted" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <History className="w-3.5 h-3.5" />
          {tape.length > 0 && !historyOpen && (
            <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setTapeOpen((v) => !v)}
          title={tapeOpen ? "Close value checker" : "Expand — check values one by one"}
          data-testid="calculator-tape-btn"
          className={`h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted ${
            tapeOpen ? "text-foreground bg-muted" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {tapeOpen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => copyVal("display", parseFloat(display) || 0)}
          title="Copy result"
          className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          {copied === "display" ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          data-testid="calculator-close"
          className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* history (the tape) — overlays the body from the header icon so it
          never pushes the keypad around */}
      {historyOpen && (
        <div
          className="absolute left-0 right-0 top-9 z-10 rounded-b-xl border-b border-border bg-card shadow-lg"
          data-testid="calculator-history"
        >
          <div className="flex items-center justify-between px-2.5 h-7 border-b border-border/60">
            <span className="text-[10px] font-semibold text-muted-foreground tracking-wide">
              HISTORY{tape.length > 0 ? ` · ${tape.length}` : ""}
            </span>
            <div className="flex items-center gap-2">
              {tape.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTape([])}
                  className="text-[10px] text-muted-foreground hover:text-destructive"
                >
                  CLEAR
                </button>
              )}
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {tape.length === 0 ? (
              <div className="px-2 py-4 text-center text-[11px] text-muted-foreground">
                No calculations yet
              </div>
            ) : (
              tape.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setDisplay(fmt(t.value)); setOverwrite(true); setHistoryOpen(false); setTab("calc"); }}
                  title="Click to reuse this value"
                  className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded hover:bg-muted/60 text-left"
                >
                  <span className="text-[10px] text-muted-foreground font-mono truncate">{t.expr}</span>
                  <span className="text-[11px] font-mono tabular-nums shrink-0">{inr(t.value)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="flex gap-0.5 m-2 mb-0 p-0.5 rounded-lg bg-muted/60">
        {tabBtn("calc", "CALC")}
        {tabBtn("gst", "GST")}
        {tabBtn("tools", "TOOLS")}
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        {/* ── CALC ──────────────────────────────────────────────────────── */}
        {tab === "calc" && (
          <>
            <div className="px-3 pt-2 pb-2.5 text-right">
              <div className="h-4 text-[11px] text-muted-foreground font-mono tabular-nums truncate">
                {acc !== null && op ? `${fmt(acc)} ${OP_LABEL[op]}` : " "}
              </div>
              <div className="text-2xl font-semibold font-mono tabular-nums truncate" data-testid="calculator-display">
                {display}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-1.5 p-2 pt-0">
              <Btn className={fnKey} onClick={clearAll} label="Clear">C</Btn>
              <Btn className={fnKey} onClick={toggleSign} label="Toggle sign">±</Btn>
              <Btn className={fnKey} onClick={percent} label="Percent">%</Btn>
              <Btn className={opKey} onClick={() => chooseOp("/")} label="Divide">÷</Btn>

              {["7", "8", "9"].map((d) => <Btn key={d} className={numKey} onClick={() => inputDigit(d)} label={d}>{d}</Btn>)}
              <Btn className={opKey} onClick={() => chooseOp("*")} label="Multiply">×</Btn>

              {["4", "5", "6"].map((d) => <Btn key={d} className={numKey} onClick={() => inputDigit(d)} label={d}>{d}</Btn>)}
              <Btn className={opKey} onClick={() => chooseOp("-")} label="Subtract">−</Btn>

              {["1", "2", "3"].map((d) => <Btn key={d} className={numKey} onClick={() => inputDigit(d)} label={d}>{d}</Btn>)}
              <Btn className={opKey} onClick={() => chooseOp("+")} label="Add">+</Btn>

              <Btn className={numKey} onClick={() => inputDigit("0")} label="0">0</Btn>
              <Btn className={numKey} onClick={inputDot} label="Decimal point">.</Btn>
              <Btn className={fnKey} onClick={backspace} label="Backspace">
                <Delete className="w-4 h-4 mx-auto" />
              </Btn>
              <Btn
                className={`${key} bg-primary text-primary-foreground hover:bg-primary/90`}
                onClick={equals}
                label="Equals"
              >
                =
              </Btn>
            </div>

          </>
        )}

        {/* ── GST ───────────────────────────────────────────────────────── */}
        {tab === "gst" && (
          <div className="p-2 space-y-2">
            {/* add vs remove (inclusive) */}
            <div className="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg bg-muted/60">
              {[["add", "Add GST"], ["remove", "Remove GST"]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setGstMode(v)}
                  className={`h-7 text-[11px] font-medium rounded-md transition-colors ${
                    gstMode === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div>
              <label className="text-[10px] font-semibold text-muted-foreground tracking-wide">
                {gstMode === "add" ? "TAXABLE VALUE" : "AMOUNT (INCL. GST)"}
              </label>
              <input
                className={fieldCls}
                inputMode="decimal"
                value={gstAmt}
                onChange={(e) => setGstAmt(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00"
                data-testid="gst-amount"
              />
            </div>

            <div>
              <label className="text-[10px] font-semibold text-muted-foreground tracking-wide">RATE %</label>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {GST_RATES.map((r) => (
                  <button key={r} type="button" className={chipCls(num(gstRate) === r)} onClick={() => setGstRate(r)}>
                    {r}%
                  </button>
                ))}
                <input
                  className="h-6 w-12 px-1 rounded border border-border bg-background text-[11px] font-mono tabular-nums text-right focus:outline-none focus:ring-2 focus:ring-primary/40"
                  inputMode="decimal"
                  value={gstRate}
                  onChange={(e) => setGstRate(e.target.value.replace(/[^0-9.]/g, ""))}
                  title="Custom rate"
                />
              </div>
            </div>

            {/* intra-state splits into CGST+SGST, inter-state is a single IGST */}
            <div className="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg bg-muted/60">
              {[[false, "CGST + SGST"], [true, "IGST"]].map(([v, l]) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => setInterState(v)}
                  className={`h-7 text-[11px] font-medium rounded-md transition-colors ${
                    interState === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="rounded-lg border border-border divide-y divide-border/60">
              <Line id="g-taxable" label="Taxable value" value={gst.taxable} note="Taxable" />
              {interState ? (
                <Line id="g-igst" label={`IGST @ ${num(gstRate)}%`} value={gst.igst} note="IGST" />
              ) : (
                <>
                  <Line id="g-cgst" label={`CGST @ ${num(gstRate) / 2}%`} value={gst.cgst} note="CGST" />
                  <Line id="g-sgst" label={`SGST @ ${num(gstRate) / 2}%`} value={gst.sgst} note="SGST" />
                </>
              )}
              <Line id="g-tax" label="Total GST" value={gst.tax} note="GST" />
              <Line id="g-total" label="Invoice total" value={gst.total} strong note="Total" />
              <Line id="g-roundoff" label="Round off" value={gst.roundOff} note="Round off" />
              <Line id="g-rounded" label="Rounded total" value={gst.rounded} strong note="Rounded total" />
            </div>
          </div>
        )}

        {/* ── TOOLS ─────────────────────────────────────────────────────── */}
        {tab === "tools" && (
          <div className="p-2 space-y-2">
            <select
              className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              data-testid="calc-tool"
            >
              {Object.entries(TOOLS).map(([k, t]) => (
                <option key={k} value={k}>{t.label}</option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground tracking-wide">
                  {toolDef.a.toUpperCase()}
                </label>
                <input
                  className={fieldCls}
                  inputMode="decimal"
                  value={toolA}
                  onChange={(e) => setToolA(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="0.00"
                  data-testid="tool-a"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground tracking-wide">
                  {toolDef.b.toUpperCase()}
                </label>
                <input
                  className={fieldCls}
                  inputMode="decimal"
                  value={toolB}
                  onChange={(e) => setToolB(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="0.00"
                  data-testid="tool-b"
                />
              </div>
            </div>

            {toolDef.chips && (
              <div className="flex flex-wrap gap-1">
                {toolDef.chips.map((c) => (
                  <button key={c} type="button" className={chipCls(num(toolB) === c)} onClick={() => setToolB(String(c))}>
                    {c}%
                  </button>
                ))}
              </div>
            )}

            <div className="rounded-lg border border-border divide-y divide-border/60">
              {toolLines.map(([label, value, strong]) => (
                <Line key={label} id={`t-${label}`} label={label} value={value} strong={strong} note={label} />
              ))}
              <Line
                id="t-rounded"
                label="Rounded"
                value={Math.round(toolLines[toolLines.length - 1][1])}
                note="Rounded"
              />
            </div>
          </div>
        )}
      </div>
    </div>

    <CalculatorTapeWindow
      open={tapeOpen}
      onClose={() => setTapeOpen(false)}
      items={reviewItems}
      anchor={{ x: pos.x, y: pos.y, w: PANEL_W }}
    />
    </>
  );
}
